import React, { useCallback } from 'react';
import { EngagementSpotlight } from '@atlassiansox/engagekit-ts';
import { Profile as AkProfileDI } from '@atlaskit/atlassian-navigation';
import AvatarDI from '@atlaskit/avatar';
import {
	SpotlightTarget as AkSpotlightTarget,
	// @ts-expect-error - TS2305 - Module '"@atlaskit/onboarding"' has no exported member 'SpotlightTargetProps'.
	type SpotlightTargetProps,
} from '@atlaskit/onboarding';
import PopupDI, { type TriggerProps } from '@atlaskit/popup';
import AccountMenuDI from '@atlassian/account-menu';
import {
	useFetchUserTraitsOld,
	useUserTraits,
} from '@atlassian/jira-business-common/src/common/providers/controllers/traits/index.tsx';
import { useExperienceStart as useExperienceStartDI } from '@atlassian/jira-experience-tracker/src/ui/experience-start/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntlV2 as useIntlDI } from '@atlassian/jira-intl/src/v2/use-intl.tsx';
import { MENU_ID } from '@atlassian/jira-navigation-apps-common/src/constants.tsx';
import { useTopMenus as useTopMenusDI } from '@atlassian/jira-navigation-apps-common/src/controllers/top-menus/index.tsx';
import {
	useAtlassianPersonalSettingsLink,
	useProfileLink,
	useSwitchAccountLink,
} from '@atlassian/jira-navigation-apps-common/src/utils/index.tsx';
import {
	testIdConcat,
	testIdGenerate,
} from '@atlassian/jira-navigation-apps-common/src/utils/test-id.tsx';
import { getWillShowNav4 } from '@atlassian/jira-navigation-apps-sidebar-nav4-rollout-core/src/common/utils/get-will-show-nav4/index.tsx';
import { NAVIGATION_APPS_PROFILE_ITEM_NUDGE_ID } from '@atlassian/jira-onboarding-core/src/constants.tsx';
import {
	useHasNudgeActive,
	useNudgeStep,
} from '@atlassian/jira-onboarding-quickstart-core/src/services/index.tsx';
import { useCurrentUser as useCurrentUserDI } from '@atlassian/jira-platform-services-user-current/src/main.tsx';
import { useThemePreference } from '@atlassian/jira-router-resources-theme-preference/src/controllers/resource-erai-1615-old/index.tsx';
import { ConditionalNudgeWrapper } from '@atlassian/jira-software-onboarding-nudges--next/src/ui/conditional-nudge-wrapper/index.tsx';
import {
	NavigationProfileNavItemWrapperNudgeAsync,
	NavigationProfileNavItemWrapperNudgeLazy,
} from '@atlassian/jira-software-onboarding-nudges--next/src/ui/navigation-profile/async.tsx';
import { LazyOpenMenuItem as LazyJswQuickstartOpenMenu } from '@atlassian/jira-software-onboarding-quickstart/src/ui/async.tsx';
import { useForgePersonalSettingsItems } from '@atlassian/jira-forge-personal-settings-items-store/src/index.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import {
	PROFILE_SPOTLIGHT_KEY,
	NAVIGATION_ITEM_ID,
	EXPERIENCE_NAVIGATION_TOP_MENU,
} from '../../common/constants.tsx';
import { TopLevelErrorBoundary } from '../../common/ui/error-boundary/index.tsx';
import { useNavigationItemAnalytics as useNavigationItemAnalyticsDI } from '../../controllers/navigation-item-analytics/main.tsx';
import { useUserBoardId as useUserBoardIdDI } from '../../controllers/user-board/index.tsx';
import { MenuNav4 as MenuNav4DI } from './menu/main-nav4.tsx';
import { Menu as MenuDI } from './menu/main.tsx';
import messages from './messages.tsx';
import type { ProfileProps } from './types.tsx';

const SpotlightTargetDI = ({ children, ...props }: SpotlightTargetProps) => (
	<AkSpotlightTarget {...props}>
		{/*
		 * The atlaskit spotlight target component merely registers a ref on the child component,
		 *  so there is no way to give it a test id. We want to keep the test id as closely coupled
		 *  with the target as possible, so we add a span with id here, rather than on the child
		 *  where it could become more easily uncoupled.
		 */}
		<span data-test-id={`ak-spotlight-target-${props.name}`}>{children}</span>
	</AkSpotlightTarget>
);

const handleMouseEnter = () => {
	LazyJswQuickstartOpenMenu.preload();
	NavigationProfileNavItemWrapperNudgeLazy.preload();
};

const Nav3Profile = ({
	AkProfile = AkProfileDI,
	Avatar = AvatarDI,
	Menu = MenuDI,
	Popup = PopupDI,
	SpotlightTarget = SpotlightTargetDI,
	useCurrentUser = useCurrentUserDI,
	useExperienceStart = useExperienceStartDI,
	useIntl = useIntlDI,
	useNavigationItemAnalytics = useNavigationItemAnalyticsDI,
	useTopMenus = useTopMenusDI,
	useUserBoardId = useUserBoardIdDI,
}: ProfileProps) => {
	const { formatMessage } = useIntl();
	const [isMenuOpen, { toggle: toggleMenu, off: closeMenu }] = useTopMenus(MENU_ID.PROFILE);
	const triggerAnalytics = useNavigationItemAnalytics({
		navigationItemId: NAVIGATION_ITEM_ID.PROFILE,
	});
	const {
		data: { user },
	} = useCurrentUser();
	const onStart = useExperienceStart({
		experience: EXPERIENCE_NAVIGATION_TOP_MENU,
		experienceId: NAVIGATION_ITEM_ID.PROFILE,
		analyticsSource: 'atlassian-navigation',
	});

	const [nudgeStep] = useNudgeStep();
	const [hasNudgeActive] = useHasNudgeActive();

	const [userBoardId, userBoardOwnerAccountId] = useUserBoardId(isMenuOpen);
	const testIdPrefix = testIdGenerate('secondary-actions', 'profile');

	const [userTraits, { setUserTraits }] = useUserTraits();

	const fetchUserTraits = useFetchUserTraitsOld();
	const [, { load: loadForgePersonalSettingsItem }] = useForgePersonalSettingsItems();
	const cloudId = useCloudId();

	const setUserTraitsIfNotAvailable = useCallback(async () => {
		if (user.isAnonymous) return;

		if (!userTraits?.attributes?.length && userTraits?.attributes !== null) {
			const newTraits = await fetchUserTraits();
			// We must explicitly check for false because fetchUserTraits returns "false" when pending, and "null" when traits are empty.
			if (newTraits !== false) {
				setUserTraits(newTraits);
			}
		}
	}, [user.isAnonymous, userTraits?.attributes, fetchUserTraits, setUserTraits]);

	const onClick = useCallback(() => {
		onStart();
		loadForgePersonalSettingsItem({ cloudId, isAnonymous: user.isAnonymous });
		toggleMenu();
		triggerAnalytics();
	}, [
		onStart,
		toggleMenu,
		triggerAnalytics,
		loadForgePersonalSettingsItem,
		cloudId,
		user.isAnonymous,
	]);

	const content = useCallback(
		() => (
			<Menu
				userBoardId={userBoardId}
				userBoardOwnerAccountId={userBoardOwnerAccountId}
				testIdPrefix={testIdPrefix}
			/>
		),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[userBoardId, userBoardOwnerAccountId, testIdPrefix],
	);

	const Trigger = useCallback(
		(triggerProps: Partial<TriggerProps>) => (
			// eslint-disable-next-line jsx-a11y/no-static-element-interactions
			<div
				data-testid={testIdConcat(testIdPrefix, 'menu-trigger')}
				onMouseOver={setUserTraitsIfNotAvailable}
				onFocus={setUserTraitsIfNotAvailable}
			>
				{/* Nudges are not rendered on the server, but they were imported, that added more weight to ssr */}
				<ConditionalNudgeWrapper
					conditionsToApplyWrapper={
						NAVIGATION_APPS_PROFILE_ITEM_NUDGE_ID === nudgeStep && hasNudgeActive
					}
					Wrapper={NavigationProfileNavItemWrapperNudgeAsync}
				>
					<AkProfile
						{...triggerProps}
						icon={
							// NOTE: This SpotlightTarget can be used throughout Jira not just within
							// the current package. They're often used for onboarding or changeboarding
							// experiences and allow an Atlaskit spotlight to be attached to page elements.
							// If you need to change this, make sure you check where the target ID is being used
							// and contact the relevant team as necessary. This Sourcegraph link should help :)
							// http://go/src/PROFILE_SPOTLIGHT_KEY
							<SpotlightTarget name={PROFILE_SPOTLIGHT_KEY}>
								<Avatar
									// @ts-expect-error - TS2339 - Property 'avatarUrls' does not exist on type 'DataBasic | (DataBasic & ResponseSuccess)'. | TS2339 - Property 'avatarUrls' does not exist on type 'DataBasic | (DataBasic & ResponseSuccess)'.
									src={user.avatarUrls ? user.avatarUrls['48x48'] : undefined}
									size="small"
									borderColor="transparent"
								/>
							</SpotlightTarget>
						}
						isSelected={isMenuOpen}
						onClick={onClick}
						tooltip={formatMessage(messages.tooltip)}
						onMouseEnter={handleMouseEnter}
						aria-label={formatMessage(messages.tooltip)}
						id="atlassian-navigation.ui.profile.icon"
					/>
				</ConditionalNudgeWrapper>
				<EngagementSpotlight engagementId={PROFILE_SPOTLIGHT_KEY} />
			</div>
		),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[formatMessage, isMenuOpen, onClick, testIdPrefix, user, nudgeStep, hasNudgeActive],
	);

	if (user.isAnonymous) return null;

	if (__SERVER__) return <Trigger aria-expanded={false} aria-haspopup />;

	return (
		<Popup
			content={content}
			isOpen={isMenuOpen}
			onClose={closeMenu}
			placement="bottom-end"
			shouldRenderToParent
			trigger={Trigger}
			label={formatMessage(messages.tooltip)}
			role="menu"
		/>
	);
};

const Nav4Profile = ({
	AccountMenu = AccountMenuDI,
	AkProfile = AkProfileDI,
	Avatar = AvatarDI,
	MenuNav4 = MenuNav4DI,
	useCurrentUser = useCurrentUserDI,
	useIntl = useIntlDI,
	useUserBoardId = useUserBoardIdDI,
}: ProfileProps) => {
	const { formatMessage } = useIntl();
	const { theme, setTheme } = useThemePreference();
	const {
		data: { user },
	} = useCurrentUser();

	// @ts-expect-error - TS2339 - Property 'emailAddress' does not exist on type 'DataBasic | (DataBasic & User)'.
	const emailAddress = user.emailAddress || '';
	const atlassianSettingsLink = useAtlassianPersonalSettingsLink(emailAddress);
	const switchAccountLink = useSwitchAccountLink();
	const profileLink = useProfileLink();

	const [userBoardId, userBoardOwnerAccountId] = useUserBoardId(false);
	const testIdPrefix = testIdGenerate('secondary-actions', 'profile');

	if (user.isAnonymous) return null;

	if (__SERVER__ && !fg('jira_nav4_beta_drop_2'))
		return (
			<AkProfile
				aria-haspopup
				aria-expanded={false}
				testId={testIdConcat(testIdPrefix, 'trigger')}
				icon={<Avatar size="small" borderColor="transparent" />}
				tooltip={formatMessage(messages.tooltip)}
				aria-label={formatMessage(messages.tooltip)}
				id="atlassian-navigation.ui.profile.icon"
			/>
		);

	return (
		<AccountMenu
			testId={testIdPrefix}
			// @ts-expect-error - TS2339 - Property 'displayName' does not exist on type 'DataBasic | (DataBasic & User)'.
			name={user.displayName}
			// @ts-expect-error - TS2339 - Property 'avatarUrls' does not exist on type 'DataBasic | (DataBasic & User)'.
			picture={user.avatarUrls ? user.avatarUrls['48x48'] : undefined}
			email={emailAddress}
			viewProfileLink={profileLink}
			manageProfileLink={atlassianSettingsLink}
			switchAccountLink={switchAccountLink}
			logOutLink="/logout"
			onThemeChange={setTheme}
			currentTheme={theme}
			extraMenuItems={
				<MenuNav4
					userBoardId={userBoardId}
					userBoardOwnerAccountId={userBoardOwnerAccountId}
					testIdPrefix={testIdPrefix}
				/>
			}
		/>
	);
};

const Profile = (props: ProfileProps) =>
	getWillShowNav4() ? <Nav4Profile {...props} /> : <Nav3Profile {...props} />;

const ProfileWithErrorBoundary = (props: ProfileProps) => (
	<TopLevelErrorBoundary id={NAVIGATION_ITEM_ID.PROFILE}>
		<Profile {...props} />
	</TopLevelErrorBoundary>
);

export { ProfileWithErrorBoundary as Profile };
